import React, { useState } from 'react';
import { PendingAction } from '../types';
import {
  X, CheckCircle2, XCircle, ShieldAlert, Brain, Building, Clock,
  TrendingUp, Database, Activity, FileText, Zap, Cpu, AlertTriangle,
  ChevronRight, Info, Download } from 'lucide-react';

interface ActionDetailProps {
  action: PendingAction | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (actionId: string) => Promise<void>;
  onReject: (actionId: string, reason: string) => Promise<void>;
  onDownloadPacket?: (actionId: string) => Promise<unknown>;
}

const workstreamBadges: Record<string, string> = {
  marketing: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  referrals: 'bg-mantis-50 text-mantis-700 border-mantis-200',
  intake: 'bg-blue-50 text-blue-700 border-blue-200',
  scheduling: 'bg-teal-50 text-teal-700 border-teal-200',
  rcm: 'bg-amber-50 text-amber-700 border-amber-200',
  hr: 'bg-purple-50 text-purple-700 border-purple-200',
  finance: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'clinical-ops': 'bg-redwood-50 text-redwood-700 border-redwood-200',
};

const riskBadges: Record<string, string> = {
  low: 'bg-mantis-50 text-mantis-700 border-mantis-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-redwood-50 text-redwood-700 border-redwood-200 font-semibold',
};

const confidenceColors = (conf: number) => {
  if (conf >= 0.85) return 'text-mantis-700 bg-mantis-50 border-mantis-200';
  if (conf >= 0.65) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-orange-50 text-orange-700 border-orange-200';
};

const confidenceBarColor = (conf: number) => {
  if (conf >= 0.85) return 'bg-mantis-500';
  if (conf >= 0.65) return 'bg-amber-500';
  return 'bg-orange-500';
};

// Friendly labels for common source data keys
const keyLabels: Record<string, string> = {
  claimId: 'Claim ID',
  claimNumber: 'Claim Number',
  cptCode: 'CPT Code',
  claimAmount: 'Claim Amount',
  denialReason: 'Denial Reason',
  denialCode: 'Denial Code',
  payerName: 'Payer',
  patientName: 'Patient',
  icd10: 'ICD-10',
  orderingProvider: 'Ordering Provider',
  insuranceVerified: 'Insurance Verified',
  urgent: 'Urgent',
  campaignName: 'Campaign',
  channel: 'Channel',
  spend: 'Spend',
  roi: 'ROI',
  cpa: 'Cost/Acquisition',
  conversionRate: 'Conversion Rate',
  sourceName: 'Referral Source',
  partnerName: 'Partner',
  referralCount: 'Referral Count',
  daysOverdue: 'Days Overdue',
  amount: 'Amount',
  vendorName: 'Vendor',
  invoiceNumber: 'Invoice #',
  providerName: 'Provider',
  facilityName: 'Facility',
  licenseType: 'License Type',
  verificationStatus: 'Verification Status',
  docType: 'Document Type',
  issue: 'Issue',
  severity: 'Severity',
  patientSafetyFlag: 'Patient Safety Flag',
  scheduledDate: 'Scheduled Date',
  noShowRate: 'No-Show Rate',
  slotType: 'Slot Type',
  location: 'Location',
  missingItems: 'Missing Items',
  formType: 'Form Type',
  completedFields: 'Completed Fields',
  totalFields: 'Total Fields',
  staffName: 'Staff Member',
  role: 'Role',
  startDate: 'Start Date',
  endDate: 'End Date',
  entityId: 'Entity ID',
  itemId: 'Item ID',
  entityName: 'Entity Name',
  name: 'Name',
  type: 'Type',
  recommendation: 'AI Recommendation',
  appealText: 'Appeal Text',
};

const formatDetailKey = (key: string) => keyLabels[key] || key
  .replace(/([A-Z])/g, ' $1')
  .replace(/^./, str => str.toUpperCase())
  .trim();

const formatDetailValue = (val: any) => {
  if (typeof val === 'boolean') return val ? '✓ Yes' : '✗ No';
  if (typeof val === 'number') return val.toLocaleString();
  if (typeof val === 'string' && val.length > 200) return val.slice(0, 200) + '…';
  return String(val);
};

// Check if a key is a "high-signal" field that should be highlighted
const isHighSignal = (key: string) => [
  'denialReason', 'claimAmount', 'payerName', 'patientName', 'cptCode',
  'amount', 'daysOverdue', 'severity', 'patientSafetyFlag', 'noShowRate',
  'conversionRate', 'roi', 'spend', 'urgent',
].includes(key);

export const ActionDetail: React.FC<ActionDetailProps> = ({
  action,
  isOpen,
  onClose,
  onApprove,
  onReject,
  onDownloadPacket,
}) => {
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'rationale' | 'data' | 'audit'>('rationale');

  if (!isOpen || !action) return null;

  const wsClass = workstreamBadges[action.workstream] ?? 'bg-slate-100 text-slate-700 border-slate-200';
  const riskClass = riskBadges[action.riskLevel] ?? 'bg-slate-100 text-slate-700 border-slate-200';
  const aiConfidence = action.aiConfidence ?? 0;
  const confClass = confidenceColors(aiConfidence);
  const modelUsed = action.modelUsed ?? 'unknown';
  const sourceData = action.sourceData ?? {};
  const entityDetails = action.targetEntity?.details ?? {};

  // Separate high-signal vs regular source data fields
  const sourceEntries = Object.entries(sourceData).filter(([k]) =>
    !['runId', 'recommendation', 'appealText', 'itemId'].includes(k)
  );
  const highSignalEntries = sourceEntries.filter(([k]) => isHighSignal(k));
  const regularEntries = sourceEntries.filter(([k]) => !isHighSignal(k));

  // Full appeal text / recommendation for display
  const fullRecommendation = sourceData.recommendation || sourceData.appealText || action.rationale || '';

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await onApprove(action.actionId);
      onClose();
    } catch (err) {
      console.error('Approval failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectReason.trim()) return;
    setIsSubmitting(true);
    try {
      await onReject(action.actionId, rejectReason);
      setRejecting(false);
      setRejectReason('');
      onClose();
    } catch (err) {
      console.error('Rejection failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-500/30 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto my-auto">
        {/* ── Modal Header ── */}
        <div className="sticky top-0 bg-white border-b border-slate-100 p-5 z-10">
          <div className="flex items-start justify-between">
            <div className="space-y-1.5 flex-1">
              {/* Badges row */}
              <div className="flex items-center flex-wrap gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${wsClass}`}>
                  {(action.workstream ?? 'workstream').toUpperCase()}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${riskClass}`}>
                  <ShieldAlert className="w-3 h-3 inline mr-1" />
                  {(action.riskLevel ?? 'medium').toUpperCase()} RISK
                </span>
                {aiConfidence > 0 && (
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${confClass}`}>
                    <Brain className="w-3 h-3 inline mr-1" />
                    {Math.round(aiConfidence * 100)}% CONFIDENCE
                  </span>
                )}
              </div>
              {/* Title */}
              <h2 className="text-lg font-bold text-slate-800">{action.actionType ?? 'Action Details'}</h2>
              {/* Meta row */}
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                <span>Agent: <strong className="text-slate-700">{action.agentName ?? 'Unknown Agent'}</strong></span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center">
                  <Clock className="w-3 h-3 mr-1 text-slate-400" />
                  {action.proposedAt ? new Date(action.proposedAt).toLocaleString() : 'Recently'}
                </span>
                {modelUsed && modelUsed !== 'unknown' && (
                  <>
                    <span className="text-slate-300">•</span>
                    <span className="flex items-center">
                      <Cpu className="w-3 h-3 mr-1 text-slate-400" />
                      <span className="font-mono text-[11px]">{modelUsed}</span>
                    </span>
                  </>
                )}
                {action.portcoId && (
                  <>
                    <span className="text-slate-300">•</span>
                    <span className="flex items-center">
                      <Building className="w-3 h-3 mr-1 text-slate-400" />
                      {action.portcoId}
                    </span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── Modal Body ── */}
        <div className="p-5 space-y-4">
          {/* Action Summary — what the AI wants to do */}
          <div className="bg-slate-50 rounded-lg p-3.5 border border-slate-200">
            <h3 className="font-semibold text-slate-700 text-xs mb-1.5 flex items-center">
              <FileText className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
              What the AI Proposes
            </h3>
            <p className="text-slate-600 leading-relaxed text-xs">{action.description ?? 'No description provided.'}</p>
          </div>

          {/* Impact Callout */}
          {action.estimatedImpact && (
            <div className="bg-mantis-50 border border-mantis-200 text-mantis-800 rounded-lg p-3 text-xs font-medium flex items-start">
              <TrendingUp className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-bold">Estimated Impact: </span>
                {action.estimatedImpact}
              </div>
            </div>
          )}

          {/* Quick Stats Grid — confidence + model + risk at a glance */}
          <div className="grid grid-cols-3 gap-2">
            {/* Confidence gauge */}
            <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
              <p className="text-[10px] text-slate-500 mb-1">AI Confidence</p>
              <div className="flex items-center justify-center space-x-1.5">
                <div className="flex-1 max-w-[60px] h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${confidenceBarColor(aiConfidence)}`}
                    style={{ width: `${aiConfidence * 100}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-slate-800">{Math.round(aiConfidence * 100)}%</span>
              </div>
            </div>
            {/* Model used */}
            <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
              <p className="text-[10px] text-slate-500 mb-1">Model</p>
              <p className="text-xs font-bold text-slate-800 font-mono truncate">
                {modelUsed !== 'unknown' ? modelUsed : '—'}
              </p>
            </div>
            {/* Risk level */}
            <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
              <p className="text-[10px] text-slate-500 mb-1">Risk Level</p>
              <p className={`text-xs font-bold uppercase ${
                action.riskLevel === 'high' || action.riskLevel === 'critical' ? 'text-orange-600' :
                action.riskLevel === 'medium' ? 'text-amber-600' : 'text-mantis-600'
              }`}>
                {action.riskLevel ?? 'medium'}
              </p>
            </div>
          </div>

          {/* Target Entity — what entity this action affects */}
          {action.targetEntity && (
            <div className="bg-slate-50 rounded-lg p-3.5 border border-slate-200">
              <h3 className="font-semibold text-slate-700 text-xs mb-2 flex items-center">
                <Building className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
                Target Entity
                {action.targetEntity.type && (
                  <span className="ml-2 text-slate-400 font-normal">({action.targetEntity.type})</span>
                )}
              </h3>
              <div className="space-y-1">
                <p className="text-xs text-slate-800 font-semibold">
                  {action.targetEntity.name ?? 'N/A'}
                </p>
                {Object.keys(entityDetails).length > 0 && (
                  <div className="grid grid-cols-2 gap-1.5 mt-2">
                    {Object.entries(entityDetails).map(([key, val]) => {
                      const isHS = isHighSignal(key);
                      return key !== 'aiRecommendation' && val !== null && val !== undefined && (
                        <div
                          key={key}
                          className={`text-[11px] flex justify-between rounded px-2 py-1 border ${
                            isHS
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-white border-slate-100'
                          }`}
                        >
                          <span className="text-slate-500">{formatDetailKey(key)}</span>
                          <span className={`font-medium ml-2 text-right ${isHS ? 'text-slate-800' : 'text-slate-700'}`}>
                            {formatDetailValue(val)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* High-signal source data preview — key facts the AI used */}
          {highSignalEntries.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5">
              <h3 className="font-semibold text-amber-800 text-xs mb-1 flex items-center">
                <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                Key Facts the AI Analyzed
              </h3>
              <div className="grid grid-cols-2 gap-1.5">
                {highSignalEntries.map(([key, val]) => (
                  <div key={key} className="text-[11px] flex justify-between bg-white rounded px-2 py-1 border border-amber-100">
                    <span className="text-amber-700">{formatDetailKey(key)}</span>
                    <span className="text-slate-800 font-medium ml-2 text-right">{formatDetailValue(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Tabbed Section: AI Reasoning / Source Data / Audit Trail ── */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            {/* Tab Bar */}
            <div className="flex border-b border-slate-200 bg-slate-50">
              <button
                onClick={() => setActiveTab('rationale')}
                className={`px-4 py-2 text-xs font-semibold transition-colors flex items-center ${
                  activeTab === 'rationale' ? 'text-mantis-700 border-b-2 border-mantis-500 bg-white' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Brain className="w-3.5 h-3.5 mr-1.5" />
                AI Reasoning
              </button>
              <button
                onClick={() => setActiveTab('data')}
                className={`px-4 py-2 text-xs font-semibold transition-colors flex items-center ${
                  activeTab === 'data' ? 'text-mantis-700 border-b-2 border-mantis-500 bg-white' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Database className="w-3.5 h-3.5 mr-1.5" />
                Source Data
                {sourceEntries.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-slate-200 text-[9px] text-slate-600">
                    {sourceEntries.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`px-4 py-2 text-xs font-semibold transition-colors flex items-center ${
                  activeTab === 'audit' ? 'text-mantis-700 border-b-2 border-mantis-500 bg-white' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Activity className="w-3.5 h-3.5 mr-1.5" />
                Audit Trail
                {action.auditTrail && action.auditTrail.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-slate-200 text-[9px] text-slate-600">
                    {action.auditTrail.length}
                  </span>
                )}
              </button>
            </div>

            <div className="p-4">
              {/* AI Reasoning Tab */}
              {activeTab === 'rationale' && (
                <div className="space-y-3">
                  {/* Confidence header */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">AI Reasoning</h4>
                      {aiConfidence > 0 && (
                        <div className="flex items-center space-x-2">
                          <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${confidenceBarColor(aiConfidence)}`}
                              style={{ width: `${aiConfidence * 100}%` }}
                            />
                          </div>
                          <span className={`text-[11px] font-bold ${
                            aiConfidence >= 0.85 ? 'text-mantis-700' :
                            aiConfidence >= 0.65 ? 'text-amber-700' : 'text-orange-700'
                          }`}>
                            {Math.round(aiConfidence * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-slate-600 leading-relaxed text-xs whitespace-pre-line bg-slate-50 rounded-md p-2.5 border border-slate-100">
                      {fullRecommendation || 'No rationale available.'}
                    </p>
                  </div>

                  {/* Historical Context */}
                  {action.historicalContext && (action.historicalContext.pastActionsCount > 0 || action.historicalContext.successRate !== 'N/A') && (
                    <div>
                      <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">Historical Context</h4>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-slate-50 rounded-md p-2 border border-slate-100 text-center">
                          <p className="text-[10px] text-slate-500">Past Actions</p>
                          <p className="text-sm font-bold text-slate-800">{action.historicalContext.pastActionsCount || 0}</p>
                        </div>
                        <div className="bg-slate-50 rounded-md p-2 border border-slate-100 text-center">
                          <p className="text-[10px] text-slate-500">Success Rate</p>
                          <p className="text-sm font-bold text-mantis-700">{action.historicalContext.successRate || 'N/A'}</p>
                        </div>
                        <div className="bg-slate-50 rounded-md p-2 border border-slate-100 text-center">
                          <p className="text-[10px] text-slate-500">Last Run</p>
                          <p className="text-xs font-semibold text-slate-700">
                            {action.historicalContext.lastExecutedAt
                              ? new Date(action.historicalContext.lastExecutedAt).toLocaleDateString()
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                      {action.historicalContext.similarActionSummary && (
                        <p className="text-[11px] text-slate-500 mt-2 italic bg-amber-50 border border-amber-100 rounded-md p-2">
                          {action.historicalContext.similarActionSummary}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Source Data Tab */}
              {activeTab === 'data' && (
                <div className="space-y-2">
                  {sourceEntries.length > 0 ? (
                    <>
                      <p className="text-[10px] text-slate-500 italic mb-2 flex items-center">
                        <Info className="w-3 h-3 mr-1" />
                        The AI analyzed this data to generate the recommendation. High-signal fields are highlighted.
                      </p>
                      {/* Render as structured key-value pairs, not raw JSON */}
                      <div className="grid grid-cols-2 gap-1.5">
                        {sourceEntries.map(([key, val]) => {
                          const isHS = isHighSignal(key);
                          return (
                            <div
                              key={key}
                              className={`text-[11px] flex justify-between rounded px-2 py-1.5 border ${
                                isHS ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'
                              }`}
                            >
                              <span className={isHS ? 'text-amber-700 font-medium' : 'text-slate-500'}>
                                {formatDetailKey(key)}
                              </span>
                              <span className={`ml-2 text-right ${isHS ? 'text-slate-800 font-medium' : 'text-slate-700'}`}>
                                {formatDetailValue(val)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {/* Full appeal text if available */}
                      {(sourceData.appealText || sourceData.recommendation) && (
                        <details className="mt-3">
                          <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700 font-medium">
                            Show full AI-generated appeal text
                          </summary>
                          <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded-md p-2.5">
                            <p className="text-[11px] text-slate-700 leading-relaxed whitespace-pre-line">
                              {sourceData.appealText || sourceData.recommendation}
                            </p>
                          </div>
                        </details>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-slate-500 italic">No source data available for this action.</p>
                  )}
                </div>
              )}

              {/* Audit Trail Tab */}
              {activeTab === 'audit' && (
                <div className="space-y-2">
                  {action.auditTrail && action.auditTrail.length > 0 ? (
                    action.auditTrail.map((entry, idx) => (
                      <div key={idx} className="flex items-start space-x-3 pb-3 border-b border-slate-100 last:border-0">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          entry.event.includes('Approved') ? 'bg-mantis-500' :
                          entry.event.includes('Rejected') ? 'bg-redwood-500' : 'bg-blue-400'
                        }`} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-slate-800">{entry.event}</p>
                            <span className="text-[10px] text-slate-400">
                              {new Date(entry.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            <span className="font-medium text-slate-700">{entry.actor}</span>
                            {entry.details && ` — ${entry.details}`}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-500 italic">No audit trail entries.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Reject Reason Form */}
          {rejecting && (
            <form onSubmit={handleRejectSubmit} className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
              <label className="block text-xs font-semibold text-slate-800">Rejection Reason (required)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejection. This will be logged in the audit trail."
                className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs text-slate-800 focus:outline-none focus:border-mantis-500 h-20"
                required
              />
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setRejecting(false)}
                  className="px-3 py-1 text-xs font-medium text-slate-500 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-redwood-500 hover:bg-redwood-400 text-white transition-colors disabled:opacity-50"
                >
                  Submit Rejection
                </button>
              </div>
            </form>
          )}
        </div>

        {/* ── Modal Footer ── */}
        {!rejecting && (
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors"
            >
              Close
            </button>
            {action.status === 'approved' ? (
              onDownloadPacket ? (
                <button
                  onClick={() => onDownloadPacket(action.actionId)}
                  className="flex items-center space-x-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-mantis-600 hover:bg-mantis-500 text-white transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download packet</span>
                </button>
              ) : null
            ) : (
              <>
                <button
                  disabled={isSubmitting}
                  onClick={() => setRejecting(true)}
                  className="flex items-center space-x-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-redwood-500 hover:bg-redwood-400 text-white transition-colors disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Reject</span>
                </button>
                <button
                  disabled={isSubmitting}
                  onClick={handleApprove}
                  className="flex items-center space-x-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-mantis-600 hover:bg-mantis-500 text-white transition-colors disabled:opacity-50"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Approve</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
